import React from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { MerchantQueryParamName } from "src/merchants/objects/MerchantQueryParam";
import {
  type IRaangeLabelProps,
  type IRaangeLabelWithFAProps,
  RaangeLabel,
} from "src/shared/components/RaangeLabel";
import { BooleanStatusFormatter } from "src/shared/formatters/BooleanStatusFormatter";
import type ILinkable from "src/shared/formatters/ILinkable";
import { useStoreDetailViewPathHandler } from "src/stores/hooks/useStorePathHandlers";
import type { IStoreAbstract, Store } from "src/stores/objects/Store";

export const StoreLabel = (props: IRaangeLabelWithFAProps) => {
  return (
    <RaangeLabel fa="store-alt" {...props}>
      <FormattedMessage id="stores" />
    </RaangeLabel>
  );
};

export interface IStoreNameFormatterProps extends ILinkable {
  children: IStoreAbstract;
  showsStoreNumber?: boolean;
}

export const StoreName = ({
  children: store,
  showsStoreNumber,
  hasLink = false,
  ...rest
}: IStoreNameFormatterProps) => {
  const { storeName, storeNumber } = store;
  const storeDetailViewPathHandler = useStoreDetailViewPathHandler();
  const name = (
    <RaangeLabel fa="store-alt" {...rest}>
      {storeName} {showsStoreNumber && storeNumber && `- #${storeNumber}`}
    </RaangeLabel>
  );
  if (hasLink) {
    return (
      <Link
        to={storeDetailViewPathHandler(store.storeUUID, (params) => {
          params.set(MerchantQueryParamName, store.merchantUUID);
          return params;
        })}
      >
        {name}
      </Link>
    );
  } else {
    return name;
  }
};

export interface IStoreFormatterProps extends Omit<IRaangeLabelProps, "fa"> {
  children: Store;
}

export const StoreCreate = (props: IRaangeLabelWithFAProps) => {
  return (
    <RaangeLabel fa="plus" {...props}>
      <FormattedMessage id="stores.create" />
    </RaangeLabel>
  );
};

export const StoreEdit = (props: IRaangeLabelWithFAProps) => {
  return (
    <RaangeLabel fa="pencil-alt" {...props}>
      <FormattedMessage id="stores.edit" />
    </RaangeLabel>
  );
};

export const StoreStatus = ({ children, ...rest }: IStoreFormatterProps) => {
  return (
    <BooleanStatusFormatter {...rest}>
      {children.storeStatus}
    </BooleanStatusFormatter>
  );
};

export const StoreSubscriberCount = ({
  children: store,
}: IStoreFormatterProps) => {
  return <FormattedNumber value={store.subscriberCount} />;
};
